import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import LockIcon from '@mui/icons-material/Lock';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';

import {
  SettingRow,
  SettingsState,
  settingsService,
} from '../services/settingsService';
import { useComuneConfigRefresh } from '../contexts/ConfigContext';


const SECTIONS: { id: string; label: string; keys: string[] }[] = [
  {
    id: 'identity',
    label: 'Identità',
    keys: [
      'identity.display_name',
      'identity.report_subject_name',
      'identity.totp_issuer_name',
      'identity.sms_sender',
    ],
  },
  {
    id: 'booking',
    label: 'Prenotazioni',
    keys: [
      'booking.base_url',
      'booking.comune_id',
      'booking.default_service_id',
    ],
  },
  {
    id: 'content',
    label: 'Contenuti dal sito',
    keys: [
      'content.scraping_base_url',
      'content.scraping_start_id',
      'content.rag_prefix',
      'content.special_doc_id',
    ],
  },
  {
    id: 'office_hours',
    label: 'Orari pubblici (API)',
    keys: ['office_hours.api_url'],
  },
  {
    id: 'features',
    label: 'Funzionalità attive',
    keys: ['features.archive', 'features.office_hours', 'features.events', 'features.rag_auto_approve'],
  },
  {
    id: 'transfer_hours',
    label: 'Trasferimento telefonico',
    keys: ['transfer_hours.enabled'],
  },
  {
    id: 'announcements',
    label: 'Avvisi Home',
    keys: [
      'announcements.nudge_after_minutes',
      'announcements.auto_archive_after_hours',
    ],
  },
  {
    id: 'events',
    label: 'Eventi',
    keys: [
      'events.base_url',
      'events.tenant',
      'events.section_page',
      'events.events_page',
    ],
  },
  {
    id: 'integrations',
    label: 'Integrazioni',
    keys: ['integrations.rag_url', 'integrations.vapi_base_url'],
  },
  {
    id: 'analysis',
    label: 'Analisi / Valutazione',
    keys: [
      'analysis.scoring_rubric',
      'analysis.critical_detection_rules',
      'analysis.macro_category_taxonomy',
      'analysis.intent_taxonomy',
      'analysis.service_taxonomy',
      'analysis.appointment_action_taxonomy',
    ],
  },
];


const ENUM_LABELS: Record<string, Record<string | number, string>> = {
  'announcements.nudge_after_minutes': {
    30: '30 minuti',
    60: '1 ora',
    120: '2 ore',
    360: '6 ore',
  },
  'announcements.auto_archive_after_hours': {
    0: 'Disabilitato',
    1: '1 ora',
    6: '6 ore',
    24: '24 ore',
  },
};


const STRUCTURAL_LABELS: Record<string, string> = {
  'identity.slug': 'Slug',
  'booking.post_mode': 'Modalità di invio prenotazioni',
  'content.scraping_strategy': 'Strategia di acquisizione contenuti',
  'office_hours.source': 'Fonte orari uffici',
};


const KEY_LABELS: Record<string, string> = {
  'identity.display_name': 'Nome visualizzato',
  'identity.report_subject_name': 'Nome nell\'oggetto dei report',
  'identity.totp_issuer_name': 'Nome nell\'app di autenticazione',
  'identity.sms_sender': 'Mittente SMS',
  'booking.base_url': 'URL servizio prenotazioni',
  'booking.comune_id': 'ID comune',
  'booking.default_service_id': 'ID servizio predefinito',
  'content.scraping_base_url': 'URL sito da cui acquisire i contenuti',
  'content.scraping_start_id': 'ID di partenza acquisizione',
  'content.rag_prefix': 'Prefisso base di conoscenza',
  'content.special_doc_id': 'ID documento speciale',
  'office_hours.api_url': 'URL API orari',
  'features.archive': 'Contenuti',
  'features.office_hours': 'Orari uffici',
  'features.events': 'Eventi',
  'transfer_hours.enabled': 'Orari di trasferimento',
  'announcements.nudge_after_minutes': 'Promemoria archiviazione dopo',
  'announcements.auto_archive_after_hours': 'Archiviazione automatica dopo',
  'events.base_url': 'URL sito eventi',
  'events.tenant': 'Ente sul sito eventi',
  'events.section_page': 'Pagina sezione eventi',
  'events.events_page': 'Pagina elenco eventi',
  'integrations.rag_url': 'URL servizio base di conoscenza',
  'integrations.vapi_base_url': 'URL API Vapi',
  'features.rag_auto_approve': 'Approvazione automatica documenti',
  'analysis.scoring_rubric': 'Rubrica di valutazione (punteggio)',
  'analysis.critical_detection_rules': 'Regole chiamata critica',
  'analysis.macro_category_taxonomy': 'Tassonomia macro-categorie',
  'analysis.intent_taxonomy': 'Tassonomia intento',
  'analysis.service_taxonomy': 'Tassonomia servizi (L3)',
  'analysis.appointment_action_taxonomy': 'Azione appuntamento (L4)',
};


function humanLabel(key: string): string {
  if (KEY_LABELS[key]) return KEY_LABELS[key];
  const last = key.split('.').slice(-1)[0];
  return last
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}


function formatTimestamp(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('it-IT');
  } catch {
    return iso;
  }
}


// Most recent active override. Resets delete the row, so they leave no trace here.
function lastModifiedOf(state: SettingsState) {
  let best: { key: string; at: string; by: string | null } | null = null;
  for (const [key, row] of Object.entries(state.overrideSafe)) {
    if (!row.isOverridden || !row.updatedAt) continue;
    if (!best || Date.parse(row.updatedAt) > Date.parse(best.at)) {
      best = { key, at: row.updatedAt, by: row.updatedBy };
    }
  }
  return best;
}


function exportSettings(state: SettingsState): void {
  const slug = String(state.structural['identity.slug']?.value ?? 'comune');
  const payload = {
    exportedAt: new Date().toISOString(),
    slug,
    lastModified: lastModifiedOf(state),
    overrideSafe: state.overrideSafe,
    structural: state.structural,
  };
  const url = URL.createObjectURL(
    new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' }),
  );
  const a = document.createElement('a');
  a.href = url;
  // sv-SE formats the local date as YYYY-MM-DD
  a.download = `impostazioni-${slug}-${new Date().toLocaleDateString('sv-SE')}.json`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}


const SettingsPage: React.FC = () => {
  const [state, setState] = useState<SettingsState | null>(null);
  const [draft, setDraft] = useState<Record<string, string | boolean | number>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [resetTarget, setResetTarget] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const refreshComuneContext = useComuneConfigRefresh();

  const load = async () => {
    try {
      const next = await settingsService.getSettings();
      setState(next);
    } catch (e: any) {
      setToast(e?.response?.data?.message || e.message || 'Errore di caricamento');
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const clearDraft = (key: string) => {
    setDraft((d) => {
      const copy = { ...d };
      delete copy[key];
      return copy;
    });
  };

  const onSave = async (key: string) => {
    if (!state) return;
    const draftValue = draft[key];
    const payload =
      draftValue !== undefined ? draftValue : state.overrideSafe[key].value;
    setSavingKey(key);
    setErrors((e) => ({ ...e, [key]: '' }));
    try {
      const next = await settingsService.updateSetting(key, payload as any);
      setState(next);
      clearDraft(key);
      setToast(`${humanLabel(key)} salvato`);
      void refreshComuneContext();
    } catch (err: any) {
      const body = err?.response?.data;
      const msg = body?.message || err.message || 'Errore';
      setErrors((e) => ({ ...e, [key]: msg }));
    } finally {
      setSavingKey(null);
    }
  };

  const onReset = async () => {
    if (!resetTarget) return;
    try {
      const next = await settingsService.deleteSetting(resetTarget);
      setState(next);
      clearDraft(resetTarget);
      setToast(`${humanLabel(resetTarget)} ripristinato al valore predefinito`);
      void refreshComuneContext();
    } catch (err: any) {
      setToast(err?.response?.data?.message || err.message);
    } finally {
      setResetTarget(null);
    }
  };

  if (!state) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh">
        <CircularProgress />
      </Box>
    );
  }

  const renderEditable = (key: string) => {
    const row: SettingRow | undefined = state.overrideSafe[key];
    if (!row) return null;
    const draftValue = draft[key];
    const dirty = draftValue !== undefined;
    const value = dirty ? draftValue : row.value;
    const isBool = row.valueType === 'bool';
    const error = errors[key];
    const meta = row.isOverridden
      ? `Aggiornato ${formatTimestamp(row.updatedAt)} da ${row.updatedBy}`
      : ' ';

    return (
      <Box
        key={key}
        sx={{
          py: 1.5,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
          flexWrap: 'wrap',
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ flex: '1 1 220px', minWidth: 200 }}>
          <Typography variant="body2" fontWeight={500}>
            {humanLabel(key)}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {key}
          </Typography>
        </Box>
        <Box sx={{ flex: '2 1 360px', minWidth: 240 }}>
          {row.enumOptions ? (
            <Box>
              <Select
                fullWidth
                size="small"
                value={value as string | number}
                error={!!error}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, [key]: e.target.value as string | number }))
                }
              >
                {row.enumOptions.map((opt) => (
                  <MenuItem key={String(opt)} value={opt}>
                    {ENUM_LABELS[key]?.[opt] ?? String(opt)}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText error={!!error}>{error || meta}</FormHelperText>
            </Box>
          ) : isBool ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Switch
                checked={Boolean(value)}
                onChange={(e) =>
                  setDraft((d) => ({ ...d, [key]: e.target.checked }))
                }
              />
              <Typography variant="body2">
                {Boolean(value) ? 'Attivo' : 'Disattivo'}
              </Typography>
              {error && (
                <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                  {error}
                </Typography>
              )}
            </Box>
          ) : (
            <TextField
              fullWidth
              size="small"
              multiline={row.multiline || undefined}
              minRows={row.multiline ? 10 : undefined}
              value={value ?? ''}
              onChange={(e) =>
                setDraft((d) => ({ ...d, [key]: e.target.value }))
              }
              error={!!error}
              helperText={error || meta}
              InputProps={
                row.multiline
                  ? { sx: { fontFamily: 'monospace', fontSize: 13 } }
                  : undefined
              }
            />
          )}
        </Box>
        <Box sx={{ flex: '0 0 auto', display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip
            label={row.isOverridden ? 'personalizzato' : 'predefinito'}
            size="small"
            color={row.isOverridden ? 'warning' : 'default'}
            variant={row.isOverridden ? 'filled' : 'outlined'}
          />
          {dirty && (
            <Button
              variant="contained"
              size="small"
              startIcon={
                savingKey === key ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <SaveIcon />
                )
              }
              onClick={() => onSave(key)}
              disabled={savingKey === key}
            >
              Salva
            </Button>
          )}
          {row.isOverridden && (
            <Tooltip title="Ripristina al valore in codice">
              <IconButton size="small" onClick={() => setResetTarget(key)}>
                <RestartAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
    );
  };

  const renderStructural = (key: string) => {
    const row = state.structural[key];
    if (!row) return null;
    return (
      <Box
        key={key}
        sx={{
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          borderTop: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ flex: '1 1 220px' }}>
          <Typography variant="body2" fontWeight={500}>
            {STRUCTURAL_LABELS[key] || humanLabel(key)}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {key}
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{ flex: '2 1 360px', fontFamily: 'monospace' }}
        >
          {String(row.value)}
        </Typography>
        <Tooltip title="Strutturale: per modificarlo serve un cambio di codice e un nuovo rilascio">
          <LockIcon fontSize="small" color="action" />
        </Tooltip>
      </Box>
    );
  };

  const lastModified = lastModifiedOf(state);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} mb={1}>
        <Typography variant="h4">Impostazioni</Typography>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={() => exportSettings(state)}
        >
          Esporta JSON
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: lastModified ? 1 : 3 }}>
        Modifica i valori di configurazione attivi senza un nuovo rilascio.
        Le modifiche diventano effettive entro 30 secondi sul backend e
        entro 60 secondi sul frontend, senza riavviare il container.
      </Typography>
      {lastModified && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Ultima modifica: {formatTimestamp(lastModified.at)} — {humanLabel(lastModified.key)}
          {lastModified.by ? ` (${lastModified.by})` : ''}
        </Typography>
      )}

      {SECTIONS.map((section) => (
        <Accordion key={section.id} defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">{section.label}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            {section.keys.map(renderEditable)}
          </AccordionDetails>
        </Accordion>
      ))}

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LockIcon fontSize="small" />
            <Typography variant="h6">Strutturali (sola lettura)</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Questi campi richiedono un cambio di codice e un nuovo rilascio.
          </Typography>
          {Object.keys(state.structural).map(renderStructural)}
        </AccordionDetails>
      </Accordion>

      <Dialog open={resetTarget !== null} onClose={() => setResetTarget(null)}>
        <DialogTitle>Ripristina al valore in codice?</DialogTitle>
        <DialogContent>
          <Typography>
            La modifica per <strong>{resetTarget}</strong> verrà rimossa dal
            database e il valore tornerà al default in codice. L'effetto è
            visibile entro 30 secondi.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetTarget(null)}>Annulla</Button>
          <Button onClick={onReset} variant="contained" color="warning">
            Ripristina
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        message={toast || ''}
      />
    </Box>
  );
};

export default SettingsPage;
